/**
 * Request Action Buttons Component
 *
 * Provides all action buttons for managing ticket requests:
 * - Approve/Deny primary and backup choices
 * - Request as Primary (sets event for Email Dana button)
 * - Archive/Unarchive
 * - Create Calendar Invite (with confirmation modal)
 * - Email Dana (with confirmation modal)
 * - Add Notes
 * - Editable ticket count dropdown
 *
 * Created: 2026-01-14
 * Updated: 2026-01-24 - Added confirmation modals, editable ticket count, Request as Primary
 */

import React, { useState, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  Archive,
  ArchiveRestore,
  Calendar,
  Mail,
  Send,
  Loader2,
  FileText,
  AlertCircle,
  RotateCcw,
  Star,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@shared/utils/cn';
import { Button } from '@shared/components/ui/Button';
import {
  useTicketRequestsStore,
  type TicketRequest,
  type ChoiceStatus,
} from '../../stores/ticketRequestsStore';
import { useHMCEventsStore, type HMCEvent } from '../../hmcEventsStore';
import {
  generateOutlookWebUrl,
  downloadCalendarInvite,
  generateCalendarInvite,
  type CalendarInviteConfig,
} from '../../utils/calendarInviteGenerator';

interface RequestActionButtonsProps {
  request: TicketRequest;
  className?: string;
}

/**
 * Generate mailto link for emailing Dana about a ticket request
 */
function generateDanaMailtoLink(request: TicketRequest, ticketCount: number): string {
  const subject = `Ticket Request - ${request.event_name} - ${request.event_date}`;

  // Build guest names list
  const guestNamesStr =
    request.guest_names && request.guest_names.length > 0
      ? request.guest_names.filter((n) => n.trim()).join(', ')
      : '(no additional guests)';

  const numGuests = request.guest_names?.filter((n) => n.trim()).length || 0;

  // Build backup options
  const backupLines: string[] = [];
  if (request.second_choice_event) {
    backupLines.push(`-${request.second_choice_event}`);
  }
  if (request.third_choice_event) {
    backupLines.push(`-${request.third_choice_event}`);
  }
  const backupSection = backupLines.length > 0 ? backupLines.join('\n') : '-None specified';

  // Build plain-text email body
  const body = `Dana,

My prospect ${request.company} would like to request ${ticketCount} tickets. This will be for ${request.full_name} with ${request.company} and their guest(s): ${guestNamesStr}.

Do you mind please marking me down as follows?

Evan Roland (${ticketCount}): ${request.title} at ${request.company} + ${numGuests} guests

If this event is unavailable, they would like to request tickets to this event as backup options:
${backupSection}
`;

  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  return `mailto:Dana.Cardwell@usi.com?subject=${encodedSubject}&body=${encodedBody}`;
}

/**
 * Status badge component
 */
function ChoiceStatusBadge({
  status,
  label,
}: {
  status: ChoiceStatus;
  label: string;
}) {
  const statusStyles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    denied: 'bg-red-100 text-red-800',
  };

  const statusIcons: Record<string, React.ReactNode> = {
    pending: <AlertCircle className="h-3 w-3" />,
    approved: <CheckCircle className="h-3 w-3" />,
    denied: <XCircle className="h-3 w-3" />,
  };

  if (!status) {
    return (
      <span className="text-xs text-dark-muted">
        {label}: Not set
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        statusStyles[status] || statusStyles.pending
      )}
    >
      {statusIcons[status]}
      {label}: {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/**
 * Confirmation Modal Component
 */
interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'default' | 'warning';
}

function ConfirmationModal({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-md">
      <div className="mx-4 w-full max-w-md rounded-xl border border-dark-border/30 bg-dark-bg p-6 shadow-2xl">
        <h3 className="mb-3 text-lg font-semibold text-white">{title}</h3>
        <p className="mb-6 text-sm text-dark-text">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onConfirm}
            className={variant === 'warning' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function RequestActionButtons({ request, className }: RequestActionButtonsProps) {
  const [statusNotes, setStatusNotes] = useState(request.status_notes || '');
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);

  // Editable ticket count (Task 4C)
  const [editableTicketCount, setEditableTicketCount] = useState(request.ticket_count);

  // Confirmation modal states (Task 4D, 4E)
  const [showOutlookConfirmModal, setShowOutlookConfirmModal] = useState(false);
  const [showEmailDanaConfirmModal, setShowEmailDanaConfirmModal] = useState(false);
  const [pendingOutlookMethod, setPendingOutlookMethod] = useState<'outlook' | 'download' | null>(null);

  const {
    updateChoiceStatus,
    updateRequestStatus,
    archiveRequest,
    unarchiveRequest,
    updateTicketCount,
    isUpdating,
  } = useTicketRequestsStore();

  const { events } = useHMCEventsStore();

  // Check if backup choices exist (separately)
  const hasSecondChoice = !!request.second_choice_event;
  const hasThirdChoice = !!request.third_choice_event;

  // Find the event for this request
  const event = useMemo(() => {
    return events.find((e) => e.id === request.event_id);
  }, [events, request.event_id]);

  // Count how many choices are already approved (Task 4B - only 1 allowed)
  const approvedCount = useMemo(() => {
    let count = 0;
    if (request.primary_choice_status === 'approved') count++;
    if (request.backup_choice_status === 'approved') count++;
    if (request.third_choice_status === 'approved') count++;
    return count;
  }, [request.primary_choice_status, request.backup_choice_status, request.third_choice_status]);
  const isPrimaryApproved = request.primary_choice_status === 'approved';
  const isBackupApproved = request.backup_choice_status === 'approved';
  const isThirdApproved = request.third_choice_status === 'approved';

  // Generate ticket count dropdown options (Task 4C)
  const ticketCountOptions = useMemo(() => {
    const options = [];
    for (let i = request.ticket_count; i >= 1; i--) {
      options.push(i);
    }
    return options;
  }, [request.ticket_count]);

  // Check if all three choices are denied (auto-deny logic)
  const checkAndUpdateOverallStatusForDeny = async (
    newPrimaryStatus: ChoiceStatus,
    newBackupStatus: ChoiceStatus,
    newThirdStatus: ChoiceStatus
  ) => {
    const effectivePrimary = newPrimaryStatus ?? request.primary_choice_status;
    const effectiveBackup = newBackupStatus ?? request.backup_choice_status;
    const effectiveThird = newThirdStatus ?? request.third_choice_status;

    const primaryDenied = effectivePrimary === 'denied';
    const backupDenied = !hasSecondChoice || effectiveBackup === 'denied';
    const thirdDenied = !hasThirdChoice || effectiveThird === 'denied';

    if (primaryDenied && backupDenied && thirdDenied) {
      await updateRequestStatus(request.request_id, 'denied', 'All event choices were denied');
    }
  };

  // Handle primary choice status update (Task 4B - check if another is already approved)
  const handlePrimaryStatus = async (status: 'approved' | 'denied') => {
    // Task 4B: Only allow one approved event
    if (status === 'approved' && approvedCount > 0 && request.primary_choice_status !== 'approved') {
      alert('Only one event can be approved per request. Please deny the other approved event first.');
      return;
    }

    await updateChoiceStatus(request.request_id, status, undefined, undefined, statusNotes || undefined);

    if (status === 'denied') {
      await checkAndUpdateOverallStatusForDeny(status, null, null);
    }
  };

  // Handle second choice (backup) status update
  const handleSecondChoiceStatus = async (status: 'approved' | 'denied') => {
    if (status === 'approved' && approvedCount > 0 && request.backup_choice_status !== 'approved') {
      alert('Only one event can be approved per request. Please deny the other approved event first.');
      return;
    }

    await updateChoiceStatus(request.request_id, undefined, status, undefined, statusNotes || undefined);

    if (status === 'denied') {
      await checkAndUpdateOverallStatusForDeny(null, status, null);
    }
  };

  // Handle third choice status update
  const handleThirdChoiceStatus = async (status: 'approved' | 'denied') => {
    if (status === 'approved' && approvedCount > 0 && request.third_choice_status !== 'approved') {
      alert('Only one event can be approved per request. Please deny the other approved event first.');
      return;
    }

    await updateChoiceStatus(request.request_id, undefined, undefined, status, statusNotes || undefined);

    if (status === 'denied') {
      await checkAndUpdateOverallStatusForDeny(null, null, status);
    }
  };

  // Handle "Request as Primary" button (Task 4A)
  const handleRequestAsPrimary = async (choiceType: 'primary' | 'second' | 'third') => {
    // This marks this choice as the one to be requested from Dana
    // For now, we'll just approve this choice and deny the others
    if (choiceType === 'primary') {
      await updateChoiceStatus(request.request_id, 'approved',
        hasSecondChoice ? 'denied' : undefined,
        hasThirdChoice ? 'denied' : undefined,
        `Primary choice set for Dana request`);
    } else if (choiceType === 'second') {
      await updateChoiceStatus(request.request_id, 'denied', 'approved',
        hasThirdChoice ? 'denied' : undefined,
        `2nd choice set for Dana request`);
    } else {
      await updateChoiceStatus(request.request_id, 'denied',
        hasSecondChoice ? 'denied' : undefined,
        'approved',
        `3rd choice set for Dana request`);
    }
  };

  // Handle reset (undo) - clear all choice statuses
  const handleReset = async () => {
    await updateChoiceStatus(request.request_id, null, null, null, statusNotes || undefined);
  };

  // Handle archive/unarchive
  const handleArchive = async () => {
    if (request.is_archived) {
      await unarchiveRequest(request.request_id);
    } else {
      await archiveRequest(request.request_id);
    }
  };

  // Handle ticket count change (Task 4C)
  const handleTicketCountChange = async (newCount: number) => {
    setEditableTicketCount(newCount);
    // Persist to backend if the store has this method
    if (updateTicketCount) {
      await updateTicketCount(request.request_id, newCount);
    }
  };

  // Handle email Dana button click (Task 4E - show confirmation modal)
  const handleEmailDanaClick = () => {
    setShowEmailDanaConfirmModal(true);
  };

  // Handle confirmed email Dana action
  const handleEmailDanaConfirmed = async () => {
    setShowEmailDanaConfirmModal(false);

    // Update status to 'requested_approval'
    const approvedChoice = request.primary_choice_status === 'approved'
      ? 'primary'
      : request.backup_choice_status === 'approved'
        ? '2nd'
        : request.third_choice_status === 'approved'
          ? '3rd'
          : null;

    const noteMessage = approvedChoice
      ? `Email sent to Dana - ${approvedChoice} choice`
      : 'Email sent to Dana - awaiting approval';

    await updateRequestStatus(request.request_id, 'requested_approval', noteMessage);

    // Then open the mailto link with editable ticket count
    const mailtoLink = generateDanaMailtoLink(request, editableTicketCount);
    window.location.href = mailtoLink;
  };

  // Handle calendar invite button click (Task 4D - show confirmation modal)
  const handleCalendarInviteClick = (method: 'outlook' | 'download') => {
    setPendingOutlookMethod(method);
    setShowOutlookConfirmModal(true);
  };

  // Handle confirmed calendar invite action
  const handleCalendarInviteConfirmed = async () => {
    setShowOutlookConfirmModal(false);
    const method = pendingOutlookMethod;
    setPendingOutlookMethod(null);

    if (!event || !method) {
      alert('Event details not found');
      return;
    }

    // Determine which option was confirmed based on choice statuses
    let optionConfirmed: 1 | 2 | 3 = 1;
    let choiceLabel = 'primary';
    if (request.primary_choice_status === 'approved') {
      optionConfirmed = 1;
      choiceLabel = 'primary';
    } else if (request.backup_choice_status === 'approved') {
      optionConfirmed = 2;
      choiceLabel = '2nd';
    } else if (request.third_choice_status === 'approved') {
      optionConfirmed = 3;
      choiceLabel = '3rd';
    }

    const config: CalendarInviteConfig = {
      event: event as HMCEvent,
      guestName: request.full_name,
      guestEmail: request.email,
      ticketsConfirmed: editableTicketCount,
      axsEmail: request.email,
      optionConfirmed,
      organizerName: 'Evan Roland',
      organizerEmail: 'evan.roland@usi.com',
    };

    // Update overall status to 'approved' - calendar invite sent means tickets confirmed
    const noteMessage = `Calendar invite ${method === 'outlook' ? 'sent via Outlook' : 'downloaded'} - ${choiceLabel} choice - ${editableTicketCount} tickets`;
    await updateRequestStatus(request.request_id, 'approved', noteMessage);

    if (method === 'outlook') {
      const url = generateOutlookWebUrl(config);
      window.open(url, '_blank');
    } else {
      const icsContent = generateCalendarInvite(config);
      const eventName = event.opponent || request.event_name;
      downloadCalendarInvite(icsContent, eventName, request.full_name);
    }
  };

  // Cancel modals
  const handleCancelOutlookModal = () => {
    setShowOutlookConfirmModal(false);
    setPendingOutlookMethod(null);
  };

  const handleCancelEmailDanaModal = () => {
    setShowEmailDanaConfirmModal(false);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Editable Ticket Count Section (Task 4C) */}
      <div className="rounded-lg border border-dark-border/30 bg-dark-surface p-4">
        <h4 className="mb-3 text-sm font-semibold text-white flex items-center justify-between">
          Ticket Count
          <div className="relative">
            <select
              value={editableTicketCount}
              onChange={(e) => handleTicketCountChange(parseInt(e.target.value))}
              className="appearance-none bg-dark-input border border-dark-border/30 rounded-xl px-4 py-2 pr-8 text-lg font-bold text-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {ticketCountOptions.map((count) => (
                <option key={count} value={count}>
                  {count} {count === 1 ? 'ticket' : 'tickets'}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-dark-muted pointer-events-none" />
          </div>
        </h4>
        <p className="text-xs text-dark-muted">
          Original request: {request.ticket_count} tickets
        </p>
      </div>

      {/* Choice Status Section */}
      <div className="rounded-lg border border-dark-border/30 bg-dark-surface p-4">
        <h4 className="mb-3 text-sm font-semibold text-white">
          Approval Status
        </h4>

        {/* Current Status Display */}
        <div className="mb-4 flex flex-wrap gap-2">
          <ChoiceStatusBadge
            status={request.primary_choice_status}
            label="Primary"
          />
          {hasSecondChoice && (
            <ChoiceStatusBadge
              status={request.backup_choice_status}
              label="2nd"
            />
          )}
          {hasThirdChoice && (
            <ChoiceStatusBadge
              status={request.third_choice_status}
              label="3rd"
            />
          )}
        </div>

        {/* Primary Choice Actions */}
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium text-dark-muted">
            Primary: {request.event_name}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={isPrimaryApproved ? 'primary' : 'secondary'}
              onClick={() => handlePrimaryStatus('approved')}
              disabled={isUpdating || isPrimaryApproved || (approvedCount > 0 && !isPrimaryApproved)}
              className={cn(
                'flex-1',
                isPrimaryApproved &&
                  'bg-green-600 hover:bg-green-700'
              )}
            >
              {isUpdating ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <CheckCircle className="mr-1 h-3 w-3" />
              )}
              Approve
            </Button>
            {/* Request as Primary Button (Task 4A) */}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleRequestAsPrimary('primary')}
              disabled={isUpdating}
              className="flex-1 border-orange-500/50 text-orange-600 hover:bg-orange-500/10"
              title="Set this as the event to request from Dana"
            >
              <Star className="mr-1 h-3 w-3" />
              Request
            </Button>
            <Button
              size="sm"
              variant={request.primary_choice_status === 'denied' ? 'primary' : 'secondary'}
              onClick={() => handlePrimaryStatus('denied')}
              disabled={isUpdating || request.primary_choice_status === 'denied'}
              className={cn(
                'flex-1',
                request.primary_choice_status === 'denied' &&
                  'bg-red-600 hover:bg-red-700'
              )}
            >
              {isUpdating ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <XCircle className="mr-1 h-3 w-3" />
              )}
              Deny
            </Button>
          </div>
        </div>

        {/* Second Choice Actions (if applicable) */}
        {hasSecondChoice && (
          <div className="mb-4">
            <p className="mb-2 text-xs font-medium text-dark-muted">
              2nd Choice: {request.second_choice_event}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={isBackupApproved ? 'primary' : 'secondary'}
                onClick={() => handleSecondChoiceStatus('approved')}
                disabled={isUpdating || isBackupApproved || (approvedCount > 0 && !isBackupApproved)}
                className={cn(
                  'flex-1',
                  isBackupApproved &&
                    'bg-green-600 hover:bg-green-700'
                )}
              >
                {isUpdating ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle className="mr-1 h-3 w-3" />
                )}
                Approve
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleRequestAsPrimary('second')}
                disabled={isUpdating}
                className="flex-1 border-orange-500/50 text-orange-600 hover:bg-orange-500/10"
                title="Set this as the event to request from Dana"
              >
                <Star className="mr-1 h-3 w-3" />
                Request
              </Button>
              <Button
                size="sm"
                variant={request.backup_choice_status === 'denied' ? 'primary' : 'secondary'}
                onClick={() => handleSecondChoiceStatus('denied')}
                disabled={isUpdating || request.backup_choice_status === 'denied'}
                className={cn(
                  'flex-1',
                  request.backup_choice_status === 'denied' &&
                    'bg-red-600 hover:bg-red-700'
                )}
              >
                {isUpdating ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <XCircle className="mr-1 h-3 w-3" />
                )}
                Deny
              </Button>
            </div>
          </div>
        )}

        {/* Third Choice Actions (if applicable) */}
        {hasThirdChoice && (
          <div className="mb-4">
            <p className="mb-2 text-xs font-medium text-dark-muted">
              3rd Choice: {request.third_choice_event}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={isThirdApproved ? 'primary' : 'secondary'}
                onClick={() => handleThirdChoiceStatus('approved')}
                disabled={isUpdating || isThirdApproved || (approvedCount > 0 && !isThirdApproved)}
                className={cn(
                  'flex-1',
                  isThirdApproved &&
                    'bg-green-600 hover:bg-green-700'
                )}
              >
                {isUpdating ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle className="mr-1 h-3 w-3" />
                )}
                Approve
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleRequestAsPrimary('third')}
                disabled={isUpdating}
                className="flex-1 border-orange-500/50 text-orange-600 hover:bg-orange-500/10"
                title="Set this as the event to request from Dana"
              >
                <Star className="mr-1 h-3 w-3" />
                Request
              </Button>
              <Button
                size="sm"
                variant={request.third_choice_status === 'denied' ? 'primary' : 'secondary'}
                onClick={() => handleThirdChoiceStatus('denied')}
                disabled={isUpdating || request.third_choice_status === 'denied'}
                className={cn(
                  'flex-1',
                  request.third_choice_status === 'denied' &&
                    'bg-red-600 hover:bg-red-700'
                )}
              >
                {isUpdating ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <XCircle className="mr-1 h-3 w-3" />
                )}
                Deny
              </Button>
            </div>
          </div>
        )}

        {/* Reset Button - only show if at least one choice status is set */}
        {(request.primary_choice_status !== null || request.backup_choice_status !== null || request.third_choice_status !== null) && (
          <div className="mb-4 border-t border-dark-border/30 pt-4">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleReset}
              disabled={isUpdating}
              className="w-full justify-center text-dark-muted hover:text-white"
            >
              {isUpdating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Reset Choice Status
            </Button>
          </div>
        )}

        {/* Notes Section */}
        <div>
          <button
            onClick={() => setIsNotesExpanded(!isNotesExpanded)}
            className="mb-2 flex items-center gap-1 text-xs font-medium text-dark-muted hover:text-white"
          >
            <FileText className="h-3 w-3" />
            {isNotesExpanded ? 'Hide Notes' : 'Add/Edit Notes'}
          </button>

          {isNotesExpanded && (
            <textarea
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
              placeholder="Add notes about this request..."
              className="w-full rounded border border-dark-border/30 p-2 text-sm"
              rows={3}
            />
          )}

          {request.status_notes && !isNotesExpanded && (
            <p className="text-xs italic text-dark-muted">
              Notes: {request.status_notes}
            </p>
          )}
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="rounded-lg border border-dark-border/30 bg-dark-surface p-4">
        <h4 className="mb-3 text-sm font-semibold text-white">
          Quick Actions
        </h4>

        <div className="grid grid-cols-2 gap-2">
          {/* Calendar Invite - Outlook */}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleCalendarInviteClick('outlook')}
            disabled={!event}
            className="justify-start"
            title={!event ? 'Event details not loaded' : 'Open in Outlook Web'}
          >
            <Calendar className="mr-2 h-4 w-4 text-brand-400" />
            Outlook Invite
          </Button>

          {/* Calendar Invite - Download ICS */}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleCalendarInviteClick('download')}
            disabled={!event}
            className="justify-start"
            title={!event ? 'Event details not loaded' : 'Download .ics file'}
          >
            <Calendar className="mr-2 h-4 w-4 text-purple-600" />
            Download .ics
          </Button>

          {/* Email Dana */}
          <Button
            size="sm"
            variant="secondary"
            onClick={handleEmailDanaClick}
            className="justify-start"
          >
            <Send className="mr-2 h-4 w-4 text-orange-600" />
            Email Dana
          </Button>

          {/* Archive/Unarchive */}
          <Button
            size="sm"
            variant="secondary"
            onClick={handleArchive}
            disabled={isUpdating}
            className={cn(
              'justify-start',
              request.is_archived && 'bg-yellow-900/20'
            )}
          >
            {isUpdating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : request.is_archived ? (
              <ArchiveRestore className="mr-2 h-4 w-4 text-yellow-600" />
            ) : (
              <Archive className="mr-2 h-4 w-4 text-dark-muted" />
            )}
            {request.is_archived ? 'Unarchive' : 'Archive'}
          </Button>
        </div>
      </div>

      {/* Email to Guest (Future) */}
      <div className="rounded-lg border border-dashed border-dark-border/30 bg-dark-surface p-4">
        <div className="flex items-center gap-2 text-sm text-dark-muted">
          <Mail className="h-4 w-4" />
          <span>Direct email to guest coming soon</span>
        </div>
      </div>

      {/* Outlook Invite Confirmation Modal (Task 4D) */}
      <ConfirmationModal
        isOpen={showOutlookConfirmModal}
        title="Change Status to Approved?"
        message={`Sending the calendar invite will mark this request as "Approved" with ${editableTicketCount} ticket(s). Do you want to proceed?`}
        confirmLabel="Yes, Approve & Send"
        onConfirm={handleCalendarInviteConfirmed}
        onCancel={handleCancelOutlookModal}
      />

      {/* Email Dana Confirmation Modal (Task 4E) */}
      <ConfirmationModal
        isOpen={showEmailDanaConfirmModal}
        title="Change Status to Requested Approval?"
        message={`Emailing Dana will change this request's status to "Requested Approval", indicating the internal request has been made and is awaiting Dana's approval. Proceed?`}
        confirmLabel="Yes, Update & Email"
        onConfirm={handleEmailDanaConfirmed}
        onCancel={handleCancelEmailDanaModal}
        variant="warning"
      />
    </div>
  );
}

export default RequestActionButtons;
